<template>
    <div>
        <claims-dashboard-icons></claims-dashboard-icons>
        <div class="u-d-flex">
            <p class="u-r-margin-top-0">Control all of your claims here. Perform searches, filters and quick edits.</p>
        </div>

        <div class="u-margin-top-20 u-margin-bottom-10">
            <filter-options
                    title="Claim Search"
                    button-title="Search"
                    :action="search"
                    :reset="reset"
                    :running="false"
                    :filter="updateFilter"
            ></filter-options>
        </div>

        <div class="u-d-flex" v-if="claims.length === 0 && !loading">
            <div class="flex-grid__g-8 u-flex-grow-none">
                <h2>No claims for your current search</h2>
            </div>
        </div>

        <div class="u-d-flex" v-if="!loading">
            <div class="flex-grid__g-8 u-flex-grow-none">
                <p>
                    Your claims are listed here, use the plus icon on the right of each claim to expand the details for
                    that claim. You can filter the table columns as well to increase or decrease information to suited
                    to your needs.</p>
            </div>
        </div>
        <br/>

        <span class="c-table-filter u-cursor-pointer" v-if="claims.length && !loading" @click="showHideFilters">Filter Table Columns</span>

        <div v-if="filters && claims.length">
            <div class="flex-grid flex-grid--reduced-bottom-margin-slight a-form-inputs flex-grid--wrap p-your-claims__bordered">
                <div class="flex-grid__g-3 p-your-claims__column-filter" v-for="columnData in availableColumns">
                    <div class="a-checkbox u-margin-top-20 u-margin-bottom-20">
                        <input autocomplete="off" type="checkbox" checked v-model="showColumns"
                               :value="columnData.value">
                        <label for="text">{{ columnData.name }}</label>
                    </div>
                </div>
            </div>
        </div>

        <div class="p-reserving__assign">
            <div :class="{ 'a-loader--table' : loading }"></div>
            <table v-if="!loading"
                   class="a-table a-table--full-width your-claims-table p-your-claims__table">
                <thead>
                <tr>
                    <th>
                        <a href="#" @click.prevent="changeOrder('your_reference')">
                            Your Reference
                        </a>
                    </th>
                    <th v-for="columnValue in showColumns">
                        <a href="#" @click.prevent="changeOrder(columnValue)">
                            {{ columnTitle(columnValue) }}
                        </a>
                    </th>
                    <th>
                        &nbsp;
                    </th>
                </tr>
                </thead>
                <tbody v-if="claims.length" v-for="claim in claims">
                <tr>
                    <td>
                        {{ claim.your_reference }}
                    </td>
                    <td v-for="columnValue in showColumns">
                        {{ output(claim, columnValue) }}
                    </td>
                    <td width="80">
                        <div class="u-d-flex u-flex-space-between p-your-claims__table-icons">
                            <div @click="toggleSummary(claim.id)" class="u-float-l u-margin-right-20">
                                <icon-tooltip :tooltip-name="'expand' + claim.id" tooltip-text="more claim details"
                                              side="top" text-width="150" icon-type="toggle"></icon-tooltip>
                            </div>

                            <div @click="sendLoad(claim)" class="u-float-l u-margin-right-20">
                                <icon-tooltip :tooltip-name="'load' + claim.id" tooltip-text="load" side="top"
                                              text-width="100" icon-type="load"></icon-tooltip>
                            </div>

                            <div @click="sendDelete(claim)" class="u-float-l" v-if="showDelete">
                                <icon-tooltip :tooltip-name="'delete' + claim.id" tooltip-text="delete" side="left"
                                              text-width="100" icon-type="delete"></icon-tooltip>
                            </div>
                        </div>
                    </td>
                </tr>
                <tr class="p-your-claims--claim-summary"
                    :class="showSummary(claim.id) ? 'u-d-table-row u-full-width' : 'u-d-none'">
                    <td v-if="showAllSummary" :colspan="showColumns.length + 2" class="u-padding-30">
                        <h2 class="summary-heading">
                            Summary of {{ claim.your_reference }}
                            <span v-if="showNames">: {{ claim.claimant_name }} v {{ claim.defendant_name}}</span>
                        </h2><br/>

                        <div class="flex-grid flex-grid--reduced-bottom-margin u-r-flex-direction-row u-r-flex-wrap">
                            <div class="flex-grid__g-3">
                                <h2>
                                    Claim Data
                                </h2>
                                <ul>
                                    <li>Claim Type: <strong>{{ claim.human_claim_type }}</strong></li>
                                    <!-- <li>Disease Type: <strong>{{ claim.disease_type }}</strong></li> -->
                                    <li>Discount Rate: <strong>{{ claim.discount_rate }}%</strong></li>
                                </ul>
                                <br/>
                                <h2 class="u-float-l">
                                    Claim Info
                                </h2>
                                <div @click="updateEditing('showClaimInfo')" class="p-your-claims--edit-icon u-float-l">
                                    <icon-tooltip :tooltip-name="'edit_claim_info' + claim.id" tooltip-text="edit"
                                                  side="right" text-width="100" icon-type="edit"></icon-tooltip>
                                </div>
                                <div class="u-clear"></div>
                                <ul>
                                    <li>Handler: <strong>{{ userFrom(claim) }}</strong></li>
                                    <li>Team: <strong>{{ teamFrom(claim) }}</strong></li>
                                    <li>Open Date: <strong>{{ dateFormat(claim.open_date, "DD/MM/YYYY") }}</strong></li>
                                    <li>Status: <strong>{{ claim.active ? 'Open' : 'Closed' }}</strong></li>
                                    <li>Outcome: <strong>{{ claim.outcome ? claim.outcome : '-' }}</strong></li>
                                </ul>
                            </div>
                            <div class="flex-grid__g-3">
                                <h2 class="u-float-l">
                                    Reserves &amp; Costs
                                </h2>
                                <div @click="updateEditing('showReservesCosts')"
                                     class="p-your-claims--edit-icon u-float-l">
                                    <icon-tooltip :tooltip-name="'edit_reserves_costs' + claim.id" tooltip-text="edit"
                                                  side="right" text-width="100" icon-type="edit"></icon-tooltip>
                                </div>
                                <div class="u-clear"></div>
                                <ul>
                                    <li>
                                        Damages Reserve: <strong>{{ formatCost(claim,
                                        'current_damages_reserve')}}</strong></li>
                                    <li>
                                        Third Party Costs Reserve: <strong>{{ formatCost(claim,
                                        'current_third_party_reserve')}}</strong></li>
                                    <li>
                                        Defence Costs Reserve: <strong>{{ formatCost(claim,
                                        'current_defence_costs_reserve')}}</strong></li>
                                    <li>
                                        Paid Damages: <strong>{{ formatCost(claim, 'paid_damages')}}</strong></li>
                                    <li>
                                        Paid Third Party Costs: <strong>{{ formatCost(claim,
                                        'paid_third_party_costs')}}</strong></li>
                                    <li>
                                        Paid Defence Costs: <strong>{{ formatCost(claim,
                                        'paid_defence_costs')}}</strong></li>
                                    <li>
                                        Recovered Costs: <strong>{{ formatCost(claim, 'recovered_costs')}}</strong></li>
                                    <li>
                                        Total Reserve: <strong>{{ formatCost(claim, 'total_current_reserve')}}</strong>
                                    </li>
                                    <li>Total Paid: <strong>{{ formatCost(claim, 'total_paid')}}</strong></li>
                                    <li>Net Paid: <strong>{{ formatCost(claim, 'net_paid')}}</strong></li>
                                </ul>
                            </div>
                            <div class="flex-grid__g-3">
                                <h2>
                                    Documents
                                </h2>
                                <ul>
                                    <a :href="'/api/v1/executive-summary/pdf/' + claim.id+'?access_token='+getToken"
                                       target="_blank"
                                    >
                                        <li>
                                            <strong>Generate Executive Summary</strong>
                                        </li>
                                    </a>
                                    <a v-if="claim.recent_schedule_of_loss"
                                       :href="'/api/v1/generated-documents/pdf/' + claim.recent_schedule_of_loss.id+'?access_token='+getToken"
                                       target="_blank"
                                    >
                                        <li>
                                            <strong>{{ claim.recent_schedule_of_loss.name }}</strong><br/>
                                            &mdash;
                                            {{ dateFormat(claim.recent_schedule_of_loss.created_at, "DD/MM/YYYY H:m") }}
                                        </li>
                                    </a>
                                    <a v-if="claim.recent_comparison_schedule"
                                       :href="'/api/v1/generated-documents/pdf/' + claim.recent_comparison_schedule.id+'?access_token='+getToken"
                                       target="_blank"
                                    >
                                        <li>
                                            <strong>{{ claim.recent_comparison_schedule.name }}</strong><br/>
                                            &mdash;
                                            {{ dateFormat(claim.recent_comparison_schedule.created_at, "DD/MM/YYYY H:m")
                                            }}
                                        </li>
                                    </a>
                                </ul>
                            </div>
                        </div>
                    </td>
                    <td v-if="showClaimInfo" :colspan="showColumns.length + 2" class="u-padding-30">
                        <claim-info :claim="claim" @close="updateEditing" @save="saveClaimChanges"></claim-info>
                    </td>
                    <td v-if="showReservesCosts" :colspan="showColumns.length + 2" class="u-padding-30">
                        <initial-reserves-costs :claim="claim" @close="updateEditing"
                                                @save="saveReserveChanges"></initial-reserves-costs>
                    </td>
                </tr>
                <tr v-if="claims.length === 0">
                    <td colspan="showColumns.length + 2" class="u-text-center">
                        No claims for your current search
                    </td>
                </tr>
                </tbody>
                <tbody v-if="!claims.length">
                <tr>
                    <td style="text-align: center" :colspan="showColumns.length + 2">
                        You do not have any claims saved at present. Click the "create a new claim" button above.
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <ul class="m-pagination" v-if="!loading">
            <li v-for="page in pageNumbers" class="m-pagination__item"
                :class="[ page == isActive ? 'm-pagination__item--active' : '']">
                <a href="#" @click.prevent="changePage(page)">
                    {{ page }}
                </a>
            </li>
        </ul>

    </div>
</template>

<script>
    import {mapActions, mapGetters} from 'vuex';
    import moment from 'moment';
    import Api from 'services/Api';
    import claimsDashboardIcons from 'pages/claims-dashboard/components/Icons';
    import FilterOptions from 'components/filter/FilterOptions';
    import ClaimInfo from 'pages/claims-dashboard/components/ClaimInfo'
    import InitialReservesCosts from 'pages/claims-dashboard/components/InitialReservesCosts'
    import IconTooltip from 'components/IconTooltip';
    import dateFormat from 'mixins/dateFormat';
    import currencyFormat from 'mixins/currencyFormat';
    import _ from 'lodash';

    export default {
        name: 'claim-table',
        props: {
            showDelete: {
                type: Boolean,
                default: false
            }
        },
        components: {
            claimsDashboardIcons,
            FilterOptions,
            IconTooltip,
            ClaimInfo,
            InitialReservesCosts
        },
        mixins: [
            dateFormat,
            currencyFormat
        ],
        created() {
            this.search();
        },
        mounted() {
            this.availableColumns.forEach((columnData) => {
                if (columnData.default === true) {
                    this.showColumns.push(columnData.value);
                }
            });
        },
        data() {
            let availableColumns = [
                {
                    name: 'Claim Type',
                    value: 'human_claim_type',
                    default: true,
                },
                {
                    name: 'Defendant Name',
                    value: 'defendant_name',
                    default: true,
                },
                {
                    name: 'Claimant Solicitor',
                    value: 'claimant_solicitor',
                    default: true,
                },
                {
                    name: 'Claimant Reference',
                    value: 'claimant_reference',
                    default: false,
                },
                {
                    name: 'Procedural Status',
                    value: 'procedural_status',
                    default: false,
                },
                {
                    name: 'Service of Proceedings',
                    value: 'service_of_proceedings',
                    default: false,
                },
                {
                    name: 'Assumed Settlement',
                    value: 'assumed_settlement',
                    default: false,
                },
                {
                    name: 'Claims Handler',
                    value: 'claims_handler',
                    default: false,
                },
                {
                    name: 'Team/Branch',
                    value: 'team_branch',
                    default: false,
                },
                {
                    name: 'Claim Status',
                    value: 'claim_status',
                    default: false,
                },
                {
                    name: 'Gender',
                    value: 'gender',
                    default: false,
                },
                {
                    name: 'Created At',
                    value: 'created_at',
                    default: false,
                },
                {
                    name: 'Updated At',
                    value: 'updated_at',
                    default: true,
                },
            ];
            if (this.showNames) {
                availableColumns.unshift(
                    {
                        name: 'Claimant Name',
                        value: 'claimant_name',
                        default: true,
                    });
            }
            return {
                loading: true,
                action: null,
                showSummaries: null,
                advancedSearchVisible: false,
                availableColumns: availableColumns,
                showColumns: [],
                pagination: {
                    total: 0,
                    per_page: 10,
                    from: 1,
                    to: 0,
                    current_page: 1,
                    last_page: 2
                },
                offset: 4,
                claims: [],
                filters: false,
                filter: {},
                order: 'updated_at',
                sort: 'desc',
                showAllSummary: true,
                showClaimInfo: false,
                showReservesCosts: false,
                summaryDefault: false,
            }
        },
        computed: {
            ...mapGetters('user', [
                'getToken',
            ]),
            ...mapGetters('http', [
                'running',
                'apiRouteRequestRunning'
            ]),
            ...mapGetters('user', ['showNames']),
            isActive: function () {
                return this.pagination.current_page;
            },
            pageNumbers: function () {
                if (!this.pagination.to) {
                    return [];
                }
                let from = this.pagination.current_page - this.offset;
                if (from < 1) {
                    from = 1;
                }
                let to = from + (this.offset * 2);
                if (to >= this.pagination.last_page) {
                    to = this.pagination.last_page;
                }
                let pagesArray = [];
                while (from <= to) {
                    pagesArray.push(from);
                    from++;
                }
                return pagesArray;
            }
        },
        methods: {
            ...mapActions('claim', [
                'load',
                'delete',
                'update',
            ]),
            showHideFilters() {
                return this.filters = !this.filters;
            },
            toggleSummary(claimId) {
                this.showSummaries = this.showSummaries !== claimId ? claimId : null;
                this.showClaimInfo = this.showReservesCosts = false;
                this.showAllSummary = true;
            },
            showSummary(claimId) {
                return this.showSummaries === claimId;
            },
            updateEditing(value) {
                this.showAllSummary = this.showClaimInfo = this.showReservesCosts = false;
                switch (value) {
                    case 'showAllSummary':
                        this.showAllSummary = true;
                        break;
                    case 'showClaimInfo':
                        this.showClaimInfo = true;
                        break;
                    case 'showReservesCosts':
                        this.showReservesCosts = true;
                        break;
                    default:
                }
            },
            search() {
                this.pagination.current_page = 0;
                return this.request();
            },
            changePage(page) {
                this.pagination.current_page = page;
                this.request();
            },
            changeOrder(column) {
                // Reset the sort order.
                this.sort = this.order != column ? 'asc' : (this.sort == 'desc' ? 'asc' : 'desc');
                this.order = column;
                this.search();
            },
            /**
             * Called by FilterOptions Reset Button Press.
             *
             * Reset's the Order By and Sort By options.
             * This is then followed by an API call to update the results.
             *
             * @return {[type]} [description]
             */
            reset() {
                // Reset button triggers resetting of order and sort
                // API call is then triggered.
                this.order = 'updated_at';
                this.sort = 'desc';
                this.filter = {};
                this.search();
            },
            columnTitle(column) {
                return this.availableColumns.filter((columnData) => {
                    return columnData.value === column;
                })[0].name;
            },
            request() {
                this.loading = true;

                return Api.claims.search(this.cleanFilter(), this.pagination.current_page, this.order, this.sort).then(claims => {
                    this.claims = claims.data;
                    this.pagination.total = claims.total;
                    this.pagination.per_page = claims.per_page;
                    this.pagination.from = claims.from;
                    this.pagination.to = claims.to;
                    this.pagination.current_page = claims.current_page;
                    this.pagination.last_page = claims.last_page;
                    this.loading = false;
                });
            },
            updateFilter({key, value}) {
                this.filter[key] = value;
            },
            cleanFilter() {
                Object.keys(this.filter).forEach((k) => {
                    if (this.filter[k] === null || this.filter[k] === "null" || this.filter[k] === "") {
                        delete this.filter[k]
                    }
                });

                return this.filter;
            },
            saveClaimChanges(data) {
                Api.claims.update(data).then((response) => {
                    let claimIndex = this.claims.findIndex((claim) => {
                        return claim.id === response.id;
                    });

                    this.claims[claimIndex]['open_date'] = moment(data.open_date, 'DD/MM/YYYY');
                    this.claims[claimIndex]['active'] = data.active;
                    this.claims[claimIndex]['outcome'] = data.outcome;
                    this.claims[claimIndex]['close_date'] = moment(data.close_date, 'DD/MM/YYYY');

                });
                this.updateEditing('showAllSummary');
            },
            saveReserveChanges(data) {
                Api.claims.updateCosts(data).then((response) => {
                    let claimIndex = this.claims.map((claim) => {
                        return claim.id;
                    }).indexOf(response.id);

                    this.claims[claimIndex].cost = response.cost;
                });
                this.updateEditing('showAllSummary');
            },
            sendLoad(claim) {
                this.action = 'loading';
                this.$emit('select', claim);
            },
            sendDelete(claim) {
                this.$emit('delete', claim);
            },
            formatCost(claim, key) {
                if (claim && claim.cost && claim.cost[key]) {
                    return this.currencyFormat(claim.cost[key]);
                }
                return '-';
            },
            userFrom(claim) {
                if (!claim.user) {
                    return;
                }

                return claim.user.name;
            },
            teamFrom(claim) {
                if (!claim.user || !claim.user.team) {
                    return;
                }

                return claim.user.team.name;
            },
            formatDate(date) {
                return moment(date).format('DD/MM/YYYY')
            },
            output(claim, column) {
                if (column === 'claims_handler') {
                    return this.userFrom(claim);
                }

                if (column === 'team_branch') {
                    return this.teamFrom(claim);
                }

                if (column === 'claim_status') {
                    return claim['active'] ? 'Open' : 'Closed';
                }

                if (column === 'service_of_proceedings') {
                    return (claim[column] !== null) ? this.formatDate(claim[column]) : 'n/a';
                }

                if (column === 'assumed_settlement') {
                    return (claim[column] !== null) ? this.formatDate(claim[column]) : 'n/a';
                }

                if (column === 'procedural_status') {
                    return claim[column] === 'pre-lit' ? 'Pre-Litigation' : 'Litigated';
                }

                if (column === 'gender') {
                    return _.upperFirst(claim[column]);
                }

                if (column === "updated_at") {
                    return this.dateFormat(claim[column], "DD/MM/YYYY H:mm");
                }

                return claim[column];
            },
        }
    }
</script>



// WEBPACK FOOTER //
// Table.vue?1088eeb5